import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Centered } from '@sorare/core/src/atoms/layout/Centered/index.tsx';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';

import { LeaderboardGroupPicker } from '#src/components/pickers/LeaderboardGroupPicker/index.tsx';
import { LeaderboardPicker } from '#src/components/pickers/LeaderboardPicker/index.tsx';
import type { Mode } from '#src/components/pickers/ModePicker/index.tsx';
import { ModePicker } from '#src/components/pickers/ModePicker/index.tsx';
import { RarityPicker } from '#src/components/pickers/RarityPicker/index.tsx';

import type { ArenaPickers_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

const StyledCentered = styled(Centered)`
  align-items: flex-end;
  gap: var(--half-unit);

  &.hasLeft > :first-child {
    justify-self: end;
  }
`;

type Props = {
  rarities?: readonly Rarity[];
  selectedRarity: Rarity;
  onSelectRarity: (rarity: Rarity, suggestedLeaderboard?: string) => void;
  modes?: readonly Mode[];
  onSelectMode: (mode: Mode) => void;
  competitionGroups?: readonly ArenaPickers_so5LeaderboardGroup[];
  selectedLeaderboardSlug?: string;
  onSelectLeaderboard?: (slug: string) => void;
};

export const ArenaPickers = ({
  rarities,
  selectedRarity,
  onSelectRarity,
  modes,
  onSelectMode,
  competitionGroups,
  selectedLeaderboardSlug,
  onSelectLeaderboard,
}: Props) => {
  const getGroupsByRarity = (rarity: Rarity) =>
    competitionGroups?.map(g => ({
      ...g,
      so5Leaderboards: g.so5Leaderboards.filter(
        l => l.mainRarityType === rarity
      ),
    })) ?? [];

  const rarityGroups = getGroupsByRarity(selectedRarity);

  const leaderboards = rarityGroups.flatMap(g => g.so5Leaderboards);

  const selectedLeaderboard =
    leaderboards.find(l => l.slug === selectedLeaderboardSlug) ??
    leaderboards[0];

  const selectedGroup = competitionGroups?.find(g =>
    g.so5Leaderboards.some(l => l.slug === selectedLeaderboardSlug)
  );

  const getSuggestedLeaderboard = (rarity: Rarity): string | undefined => {
    const newRarityGroups = getGroupsByRarity(rarity);
    const similarGroup = newRarityGroups.find(
      g => g.slug === selectedGroup?.slug
    );
    return (similarGroup ?? newRarityGroups.at(0))?.so5Leaderboards.at(0)?.slug;
  };

  const showCompetitionPickers =
    !!competitionGroups && !!onSelectLeaderboard && leaderboards.length > 1;

  return (
    <StyledCentered
      left={
        <RarityPicker
          rarities={rarities}
          selected={selectedRarity}
          onSelect={rarity =>
            onSelectRarity(rarity, getSuggestedLeaderboard(rarity))
          }
          size="sm"
        />
      }
      right={
        showCompetitionPickers ? (
          <Horizontal gap={1}>
            <LeaderboardGroupPicker
              so5LeaderboardGroups={rarityGroups}
              selectedGroup={selectedGroup?.slug}
              onSelect={onSelectLeaderboard!}
              size="sm"
            />
            <LeaderboardPicker
              leaderboards={
                rarityGroups.find(g => g.slug === selectedGroup?.slug)
                  ?.so5Leaderboards ?? []
              }
              selectedLeaderboard={selectedLeaderboard}
              onSelect={onSelectLeaderboard!}
              size="sm"
            />
          </Horizontal>
        ) : undefined
      }
    >
      <ModePicker modes={modes} onSelect={onSelectMode} />
    </StyledCentered>
  );
};

ArenaPickers.fragments = {
  so5LeaderboardGroup: gql`
    fragment ArenaPickers_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      ...LeaderboardGroupPicker_so5LeaderboardGroup
      so5Leaderboards {
        slug
        mainRarityType
        ...LeaderboardPicker_so5Leaderboard
      }
    }
    ${LeaderboardGroupPicker.fragments.so5LeaderboardGroup}
    ${LeaderboardPicker.fragments.so5Leaderboard}
  ` as TypedDocumentNode<ArenaPickers_so5LeaderboardGroup>,
};
